import { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useAdminListSystemNotices } from '@/api/auth/admin/admin';
import { useAdminGetProducts } from '@/api/auth/admin/admin';
import { AddSystemNoticeDialog } from '@/components/admin/AddSystemNoticeDialog';
import { useNavigate } from 'react-router-dom';
import { ArrowUpDown, ArrowUp, ArrowDown, Filter, Package, Activity, AlertCircle, Archive, X } from 'lucide-react';
import type { SystemNoticeResponse } from '@/api/auth/missionImpactAuthAPI.schemas';

type SortField = 'title' | 'product' | 'severity' | 'status' | 'startDate' | 'endDate';
type SortDirection = 'asc' | 'desc' | null;

interface FilterChip {
  id: string;
  type: 'product' | 'status' | 'severity' | 'archived';
  value: string;
  label: string;
}

/**
 * Admin System Notices page for managing system-wide notifications
 *
 * Features:
 * - List all system notices with filtering and sorting
 * - Summary statistics (active, urgent, scheduled counts)
 * - Create new notices via dialog
 * - Navigate to detail page for editing
 */
export default function AdminSystemNoticesPage() {
  const navigate = useNavigate();

  // Filter state
  const [filters, setFilters] = useState<FilterChip[]>([]);

  // Sorting state
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Fetch available products for filter
  const { data: productsData } = useAdminGetProducts();

  // Extract filter values for API
  const productFilters = filters.filter((f) => f.type === 'product').map((f) => f.value);
  const statusFilters = filters.filter((f) => f.type === 'status').map((f) => f.value);
  const severityFilters = filters.filter((f) => f.type === 'severity').map((f) => f.value);
  const includeArchived = filters.some((f) => f.type === 'archived');

  // Handle "Scheduled" filter - it's a UI-only status
  const hasScheduledFilter = statusFilters.includes('Scheduled');
  const apiStatusFilters = statusFilters.filter((s) => s !== 'Scheduled');

  // Fetch system notices with filters
  const {
    data: noticesData,
    isLoading,
    error,
  } = useAdminListSystemNotices({
    productId: productFilters[0] || undefined,
    status: apiStatusFilters[0] || undefined,
    severity: severityFilters[0] || undefined,
    includeArchived,
  });

  const allNotices = noticesData?.data || [];

  // Apply client-side filtering for "Scheduled" status
  const notices = useMemo(() => {
    if (!hasScheduledFilter) return allNotices;

    return allNotices.filter((notice) => {
      // Scheduled = Published with future start date
      return notice.status === 'Published' && new Date(notice.startsAt) > new Date();
    });
  }, [allNotices, hasScheduledFilter]);

  /**
   * Available filter options
   */
  const availableStatuses = [
    { value: 'Draft', label: 'Draft' },
    { value: 'Published', label: 'Published' },
    { value: 'Scheduled', label: 'Scheduled' },
    { value: 'Archived', label: 'Archived' },
  ];

  const availableSeverities = [
    { value: 'Info', label: 'Info' },
    { value: 'Urgent', label: 'Urgent' },
  ];

  /**
   * Calculate summary statistics (use allNotices, not filtered notices)
   */
  const stats = {
    active: allNotices.filter((n) => n.status === 'Published').length,
    urgent: allNotices.filter((n) => n.severity === 'Urgent').length,
    scheduled: allNotices.filter(
      (n) => n.status === 'Published' && new Date(n.startsAt) > new Date()
    ).length,
  };

  /**
   * Get badge variant based on notice status
   */
  const getStatusBadge = (notice: SystemNoticeResponse) => {
    // Show "Scheduled" for published notices with future start date
    if (notice.status === 'Published' && new Date(notice.startsAt) > new Date()) {
      return <Badge variant="outline">Scheduled</Badge>;
    }

    switch (notice.status) {
      case 'Draft':
        return <Badge variant="secondary">Draft</Badge>;
      case 'Published':
        return <Badge variant="default">Published</Badge>;
      case 'Archived':
        return <Badge variant="destructive">Archived</Badge>;
      default:
        return <Badge>{notice.status}</Badge>;
    }
  };

  /**
   * Get badge variant based on notice severity
   */
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'Info':
        return <Badge variant="secondary">Info</Badge>;
      case 'Urgent':
        return <Badge variant="destructive">Urgent</Badge>;
      default:
        return <Badge>{severity}</Badge>;
    }
  };

  /**
   * Format date range for display
   */
  const formatDateRange = (notice: SystemNoticeResponse) => {
    const start = new Date(notice.startsAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

    if (notice.endsAt) {
      const end = new Date(notice.endsAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
      return `${start} - ${end}`;
    }

    return `${start}+`;
  };

  /**
   * Get the title from the first available content language
   */
  const getNoticeTitle = (notice: SystemNoticeResponse) => {
    const englishContent = notice.contents.find((c) => c.language === 'en');
    if (englishContent) return englishContent.title;

    return notice.contents[0]?.title || 'Untitled';
  };

  /**
   * Handle column sort
   */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction or clear sort
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  /**
   * Get sort icon for column
   */
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4 opacity-30" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="ml-2 h-4 w-4" />;
    }
    return <ArrowDown className="ml-2 h-4 w-4" />;
  };

  /**
   * Sort notices based on current sort state
   */
  const sortedNotices = useMemo(() => {
    if (!sortField || !sortDirection) return notices;

    return [...notices].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'title':
          comparison = getNoticeTitle(a).localeCompare(getNoticeTitle(b));
          break;
        case 'product':
          comparison = a.productId.localeCompare(b.productId);
          break;
        case 'severity':
          comparison = a.severity.localeCompare(b.severity);
          break;
        case 'status':
          // Sort by actual status (considering scheduled)
          const getStatus = (n: SystemNoticeResponse) =>
            n.status === 'Published' && new Date(n.startsAt) > new Date()
              ? 'Scheduled'
              : n.status;
          comparison = getStatus(a).localeCompare(getStatus(b));
          break;
        case 'startDate':
          comparison = new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();
          break;
        case 'endDate':
          // Handle null endsAt (indefinite notices should go to end when sorting asc)
          if (!a.endsAt && !b.endsAt) comparison = 0;
          else if (!a.endsAt) comparison = 1;
          else if (!b.endsAt) comparison = -1;
          else comparison = new Date(a.endsAt).getTime() - new Date(b.endsAt).getTime();
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [notices, sortField, sortDirection]);

  /**
   * Add a product filter
   */
  const handleAddProductFilter = (productId: string) => {
    if (!filters.some((f) => f.type === 'product' && f.value === productId)) {
      const newFilter: FilterChip = {
        id: `product-${productId}`,
        type: 'product',
        value: productId,
        label: productId,
      };
      setFilters([...filters, newFilter]);
    }
  };

  /**
   * Add a status filter
   */
  const handleAddStatusFilter = (status: string, label: string) => {
    if (!filters.some((f) => f.type === 'status' && f.value === status)) {
      const newFilter: FilterChip = {
        id: `status-${status}`,
        type: 'status',
        value: status,
        label,
      };
      setFilters([...filters, newFilter]);
    }
  };

  /**
   * Add a severity filter
   */
  const handleAddSeverityFilter = (severity: string, label: string) => {
    if (!filters.some((f) => f.type === 'severity' && f.value === severity)) {
      const newFilter: FilterChip = {
        id: `severity-${severity}`,
        type: 'severity',
        value: severity,
        label,
      };
      setFilters([...filters, newFilter]);
    }
  };

  /**
   * Toggle archived filter
   */
  const handleToggleArchived = () => {
    if (includeArchived) {
      setFilters(filters.filter((f) => f.type !== 'archived'));
    } else {
      setFilters([
        ...filters,
        {
          id: 'archived',
          type: 'archived',
          value: 'true',
          label: 'Include Archived',
        },
      ]);
    }
  };

  /**
   * Remove a specific filter
   */
  const handleRemoveFilter = (filterId: string) => {
    setFilters(filters.filter((f) => f.id !== filterId));
  };

  /**
   * Clear all filters
   */
  const clearFilters = () => {
    setFilters([]);
  };

  /**
   * Get label for filter type
   */
  const getFilterTypeLabel = (type: FilterChip['type']): string => {
    switch (type) {
      case 'product':
        return 'Product';
      case 'status':
        return 'Status';
      case 'severity':
        return 'Severity';
      case 'archived':
        return '';
      default:
        return type;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">System Notices</h1>
          <p className="text-muted-foreground">
            Manage system-wide notifications for maintenance, policy updates, and billing changes
          </p>
        </div>
        <AddSystemNoticeDialog />
      </div>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Notices</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active}</div>
            <p className="text-xs text-muted-foreground">Published or scheduled</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Urgent Notices</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.urgent}</div>
            <p className="text-xs text-muted-foreground">Requiring acknowledgement</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Scheduled</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.scheduled}</div>
            <p className="text-xs text-muted-foreground">Future notices</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {/* Product Submenu */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Package className="h-4 w-4 mr-2" />
                  Product
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-56">
                  {!productsData?.data?.products || productsData.data.products.length === 0 ? (
                    <DropdownMenuItem disabled>No products available</DropdownMenuItem>
                  ) : (
                    productsData.data.products
                      .filter((product) => product.productId)
                      .map((product) => (
                        <DropdownMenuItem
                          key={product.productId}
                          onClick={() => handleAddProductFilter(product.productId!)}
                          disabled={filters.some(
                            (f) => f.type === 'product' && f.value === product.productId
                          )}
                        >
                          <span className="flex-1 font-mono text-sm">{product.productId}</span>
                        </DropdownMenuItem>
                      ))
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              {/* Status Submenu */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Activity className="h-4 w-4 mr-2" />
                  Status
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-56">
                  {availableStatuses.map((status) => (
                    <DropdownMenuItem
                      key={status.value}
                      onClick={() => handleAddStatusFilter(status.value, status.label)}
                      disabled={filters.some(
                        (f) => f.type === 'status' && f.value === status.value
                      )}
                    >
                      <span className="flex-1">{status.label}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              {/* Severity Submenu */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Severity
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-56">
                  {availableSeverities.map((severity) => (
                    <DropdownMenuItem
                      key={severity.value}
                      onClick={() => handleAddSeverityFilter(severity.value, severity.label)}
                      disabled={filters.some(
                        (f) => f.type === 'severity' && f.value === severity.value
                      )}
                    >
                      <span className="flex-1">{severity.label}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              <DropdownMenuSeparator />

              {/* Include Archived Toggle */}
              <DropdownMenuCheckboxItem checked={includeArchived} onCheckedChange={handleToggleArchived}>
                <Archive className="h-4 w-4 mr-2" />
                Include Archived
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Clear All Button */}
          {filters.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear All
            </Button>
          )}
        </div>

        {/* Active Filters */}
        {filters.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {filters.map((filter) => (
              <Badge
                key={filter.id}
                variant="secondary"
                className="pl-3 pr-1 py-1.5 text-sm flex items-center gap-2 hover:bg-secondary/80 cursor-pointer group"
              >
                {filter.type !== 'archived' && (
                  <>
                    <span>{getFilterTypeLabel(filter.type)}:</span>
                    <span className="font-medium">{filter.label}</span>
                  </>
                )}
                {filter.type === 'archived' && <span className="font-medium">{filter.label}</span>}
                <button
                  onClick={() => handleRemoveFilter(filter.id)}
                  className="ml-1 rounded-full p-0.5 hover:bg-secondary-foreground/20 transition-colors"
                  aria-label={`Remove ${filter.label} filter`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Notices Table */}
      {error ? (
        <div className="text-center text-destructive py-8">
          <p>
            Failed to load system notices: {(error as Error)?.message || 'Unknown error'}
          </p>
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="-ml-3 h-8 hover:bg-transparent"
                    onClick={() => handleSort('title')}
                  >
                    Title
                    {getSortIcon('title')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="-ml-3 h-8 hover:bg-transparent"
                    onClick={() => handleSort('product')}
                  >
                    Product
                    {getSortIcon('product')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="-ml-3 h-8 hover:bg-transparent"
                    onClick={() => handleSort('severity')}
                  >
                    Severity
                    {getSortIcon('severity')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="-ml-3 h-8 hover:bg-transparent"
                    onClick={() => handleSort('status')}
                  >
                    Status
                    {getSortIcon('status')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="-ml-3 h-8 hover:bg-transparent"
                    onClick={() => handleSort('startDate')}
                  >
                    Active Period
                    {getSortIcon('startDate')}
                  </Button>
                </TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(3)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton className="h-4 w-[200px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[120px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-[60px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-[80px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[100px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-[80px]" />
                    </TableCell>
                  </TableRow>
                ))
              ) : sortedNotices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    {filters.length > 0
                      ? 'No system notices match the current filters.'
                      : 'No system notices found. Create your first notice to get started.'}
                  </TableCell>
                </TableRow>
              ) : (
                sortedNotices.map((notice) => (
                  <TableRow
                    key={notice.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/dashboard/admin/system-notices/${notice.id}`)}
                  >
                    <TableCell className="font-medium">{getNoticeTitle(notice)}</TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">{notice.productId}</span>
                    </TableCell>
                    <TableCell>{getSeverityBadge(notice.severity)}</TableCell>
                    <TableCell>{getStatusBadge(notice)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateRange(notice)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dashboard/admin/system-notices/${notice.id}`);
                        }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
